import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { config } from "../environment";
import { useSessionBootstrap } from "./useSessionBootstrap";
import { nativeCallbackPath } from "../lib/apiPaths";
import { readTrustedSignInCallbackURL } from "../lib/signInCallbackURL";
import { apiUrl } from "../lib/tenantOrigin";
import { Route, routes, Switch } from "../lib/urls";
import { AppLoading } from "./AppLoading";
import { LicenseGate } from "./LicenseGate";
import { NavigationTracer } from "./NavigationTracer";
import { SignedIn } from "./SignedIn";
import { SignIn } from "./SignIn";
import { AuthError } from "./SignIn/AuthError";
import { ToastProvider } from "./Toast";
import { Version } from "./Version";
import { ZeroSetup } from "./ZeroSetup";

export function App() {
  return <AppRoutes />;
}

function AppRoutes() {
  const { hasLoaded, user } = useSessionBootstrap();

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  const isNativeCallback =
    new URLSearchParams(window.location.search).get("native") === "true";
  // If the user arrives here already signed in and there's a trusted
  // `?callbackURL=` (e.g. from the public server's SSO redirect), forward
  // them through rather than dropping them on the default landing page.
  const pendingCallbackURL =
    !isNativeCallback && hasLoaded && user
      ? readTrustedSignInCallbackURL()
      : null;

  // If the user is already signed in and this is a native app callback,
  // redirect to the native-callback endpoint to hand the session to the Mac app.
  useEffect(() => {
    if (hasLoaded && user && isNativeCallback) {
      window.location.href = `${apiUrl()}${nativeCallbackPath()}`;
    }
  }, [hasLoaded, user, isNativeCallback]);

  useEffect(() => {
    if (pendingCallbackURL) {
      window.location.href = pendingCallbackURL;
    }
  }, [pendingCallbackURL]);

  if (!hasLoaded) return <AppLoading />;
  if (isNativeCallback && user) return <AppLoading />;
  if (pendingCallbackURL) return <AppLoading />;

  return (
    <>
      <title>{config.title}</title>
      <Switch>
        <Route path={routes.version}>
          <Version />
        </Route>
        <Route path={routes.authError}>
          <ToastProvider>
            <AuthError />
          </ToastProvider>
        </Route>
        <Route>
          {user ? (
            <LicenseGate>
              <ZeroSetup user={user}>
                <NavigationTracer>
                  <SignedIn />
                </NavigationTracer>
              </ZeroSetup>
            </LicenseGate>
          ) : (
            <ToastProvider>
              <SignIn />
            </ToastProvider>
          )}
        </Route>
      </Switch>
    </>
  );
}
